import { type AuthResponseDto, type ClientConfigDto, type MeDto } from '@spin/shared';
import { game } from './api/game';
import { http, login } from './api/http';
import { useSession } from './store/session';
import { initTelegram, isTelegramLaunch, tg } from './tg/bridge';

const DEV_INIT_DATA = 'spin:dev-init-data';
/** A web portal session (password login) lives in this tab only. */
const WEB_TOKEN = 'spin:web-token';

let initData = '';

async function loadConfig(): Promise<void> {
  const config = await http.get<ClientConfigDto>('/config');
  useSession.getState().setConfig(config);
}

/** Session ready: the player, then the game connection (the table screen seats the player). */
function enter(user: MeDto, startParam: string | null, home: 'table' | 'portal'): void {
  const session = useSession.getState();
  session.setMe(user);
  session.reset({ name: home });
  session.setStatus('ready');
  routeFromLaunch(startParam);
  if (user.onboarded) game.connect();
}

/** Logs in with initData (from Telegram, or from the development login) and opens the table. */
export async function startSession(data: string): Promise<MeDto> {
  initData = data;
  const auth = await login(data);
  http.setToken(auth.token);
  const reauth = async () => {
    try {
      const fresh = await login(initData);
      http.setToken(fresh.token);
      return fresh.token;
    } catch {
      useSession.getState().setStatus('error', 'errors.INIT_DATA_EXPIRED');
      return null;
    }
  };
  http.onReauth(reauth);
  game.reauth = reauth;
  enter(auth.user, auth.startParam, 'table');
  return auth.user;
}

/** Web portal login outside Telegram (game id or @username and the password set in Services). */
export async function passwordLogin(login: string, password: string): Promise<void> {
  const auth = await http.post<AuthResponseDto>('/auth/password', { login, password });
  sessionStorage.setItem(WEB_TOKEN, auth.token);
  startWebSession(auth.token, auth.user);
}

function startWebSession(token: string, user: MeDto): void {
  http.setToken(token);
  const expired = async () => {
    logout();
    return null;
  };
  http.onReauth(expired);
  game.reauth = expired;
  enter(user, null, 'portal');
}

/** Ends a web session (back to the login page); inside Telegram the Mini App closes. */
export function logout(): void {
  sessionStorage.removeItem(WEB_TOKEN);
  sessionStorage.removeItem(DEV_INIT_DATA);
  if (tg.inTelegram) {
    tg.close();
    return;
  }
  game.disconnect();
  http.setToken(null);
  const session = useSession.getState();
  session.reset({ name: 'portal' });
  session.setStatus('login');
}

/** Deep links: bot buttons pass ?screen=table:<id>; Telegram direct links pass start_param r_<room id>. */
function routeFromLaunch(startParam: string | null): void {
  const screen = new URLSearchParams(window.location.search).get('screen');
  const tableId = screen?.startsWith('table:')
    ? screen.slice(6)
    : startParam?.startsWith('r_')
      ? startParam.slice(2)
      : null;
  if (tableId) sessionStorage.setItem('spin:join-table', tableId);
}

export async function boot(): Promise<void> {
  const session = useSession.getState();
  try {
    await loadConfig();
    if (isTelegramLaunch()) {
      await initTelegram();
      const data = tg.initData();
      if (!data) {
        session.setStatus('error', 'errors.INIT_DATA_INVALID');
        return;
      }
      await startSession(data);
      return;
    }
    // Outside Telegram: a web session of this tab, a development login, or the login page.
    const token = sessionStorage.getItem(WEB_TOKEN);
    if (token) {
      http.setToken(token);
      try {
        startWebSession(token, await http.get<MeDto>('/me'));
        return;
      } catch {
        sessionStorage.removeItem(WEB_TOKEN);
        http.setToken(null);
      }
    }
    const saved = sessionStorage.getItem(DEV_INIT_DATA);
    if (saved) {
      await startSession(saved);
      return;
    }
    session.setStatus('login');
  } catch (err) {
    sessionStorage.removeItem(DEV_INIT_DATA);
    const code = (err as { code?: string }).code ?? 'INTERNAL_ERROR';
    session.setStatus('error', `errors.${code}`);
  }
}

/** Development login (DEV_AUTH_ENABLED on the server): signs test initData for a fake Telegram user. */
export async function devLogin(input: {
  telegramId: number;
  firstName: string;
  languageCode: string;
}): Promise<void> {
  const { initData: data } = await http.post<{ initData: string }>('/dev/init-data', input);
  sessionStorage.setItem(DEV_INIT_DATA, data);
  await startSession(data);
}
